/**
 * Guest session lifecycle: create, persist, restore, expire, leave.
 *
 * Guests never touch Firebase. Their bearer token (and later the room-scoped
 * token returned by create/join) is stored under the existing `token` key via
 * BhabiStorage, exactly like account tokens, so the in-play engine
 * (websocket + REST hooks reading userReducer.token) works unchanged.
 */
import axios, { AxiosError } from 'axios';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';
import { api } from '@/api';
import { dispatch, getStoreState, Store } from '@/redux';
import { setInPlayState, updateUserState, User } from '@/redux/reducers';
import { BhabiStorage } from '@/shared/lib/storage';
import { LoggerService } from '@/shared/logger';
import {
  decodeTokenClaims,
  extractToken,
  isTokenExpired,
  roomIdFromToken,
} from './lib/tokens';
import { Invitation } from './lib/invite';
import { normalizeGuestName } from './lib/names';

const PROFILE_KEY = 'bhabiGuestProfile';
const TOKEN_KEY = 'token';

export interface GuestProfile {
  kind: 'guest';
  name: string;
  userId?: string;
  createdAt: number;
}

export type GuestUser = Partial<User> & {
  id: string;
  name: string;
  userType?: 'guest' | 'user' | 'bot';
};

// ── storage helpers ──────────────────────────────────────────────────────────

const readItem = (key: string) =>
  new Promise<string | null>(resolve => {
    BhabiStorage.getItem((value, err) => resolve(err ? null : value), key);
  });

export const loadGuestProfile = async (): Promise<GuestProfile | null> => {
  const raw = await readItem(PROFILE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as GuestProfile;
    return parsed?.kind === 'guest' && parsed.name ? parsed : null;
  } catch {
    return null;
  }
};

const saveGuestProfile = (profile: GuestProfile) =>
  new Promise<void>(resolve =>
    BhabiStorage.setItem(PROFILE_KEY, JSON.stringify(profile), () => resolve())
  );

const emptyInPlay = {
  gameInfo: { room: null, userDeck: null, inPlayCards: null, deck: null },
  players: [],
  showRequestCardsOption: false,
  concludedCards: 0,
  missingOwner: false,
  ownerUsername: '',
};

/** Forget the guest completely (used before switching to an account). */
export const clearGuestSession = async () => {
  await BhabiStorage.removeItem(TOKEN_KEY);
  await BhabiStorage.removeItem(PROFILE_KEY);
  dispatch(setInPlayState(emptyInPlay));
  dispatch(
    updateUserState({
      id: '',
      name: '',
      token: null,
      userType: undefined,
      isAuthenticated: false,
      isInRoom: false,
      guestExpired: false,
      guestName: null,
      bootError: null,
    })
  );
};

// ── error helpers ────────────────────────────────────────────────────────────

export const isAuthError = (err: unknown) => {
  const response = (err as AxiosError<{ code?: string }>)?.response;
  return (
    response?.status === 401 && response.data?.code === 'GUEST_SESSION_EXPIRED'
  );
};

export const isNetworkError = (err: unknown) =>
  axios.isAxiosError(err) && !err.response;

export const apiErrorMessage = (err: unknown, fallback: string) => {
  if (isNetworkError(err)) {
    return "Can't reach the Bhabi table right now. Check your connection and try again.";
  }
  const data = (err as AxiosError<{ message?: string; error?: string }>)
    ?.response?.data;
  if (typeof data === 'string' && data) return data;
  return data?.message || data?.error || fallback;
};

const authHeader = (token: string) => ({ Authorization: `Bearer ${token}` });

const markExpired = async (name: string | null) => {
  await BhabiStorage.removeItem(TOKEN_KEY);
  dispatch(
    updateUserState({
      token: null,
      isAuthenticated: false,
      isInRoom: false,
      isIdle: false,
      isLoading: false,
      guestExpired: true,
      guestName: name,
      bootError: null,
    })
  );
};

// ── session ──────────────────────────────────────────────────────────────────

const userUrl = () =>
  ['/user', Capacitor.getPlatform() !== 'ios' ? 'platform=web' : ''].join('?');

/**
 * Validate a stored guest token against /user and hydrate redux. Returns the
 * outcome so callers (bootstrap, reconnect) can decide what to show.
 */
export const refreshGuestUser = async (
  token: string,
  profile: GuestProfile | null
): Promise<'ok' | 'expired' | 'network'> => {
  if (isTokenExpired(token)) {
    await markExpired(profile?.name || null);
    return 'expired';
  }
  try {
    const expiresAt = decodeTokenClaims(token)?.exp;
    if (expiresAt && expiresAt * 1000 - Date.now() < 3 * 24 * 60 * 60 * 1000) {
      const renewed = await api.post(
        '/guest-session',
        {},
        {
          headers: authHeader(token),
          skipAuthRedirect: true,
        }
      );
      const renewedToken = extractToken(renewed.data);
      if (!renewedToken)
        throw new Error('Session renewal did not return a token');
      token = renewedToken;
      await BhabiStorage.setToken(token);
    }
    const res = await api.get<GuestUser & { isInRoom?: boolean }>(userUrl(), {
      headers: authHeader(token),
      skipAuthRedirect: true,
    });
    dispatch(
      updateUserState({
        ...res.data,
        userType: res.data.userType || 'guest',
        token,
        isInRoom: res.data.isInRoom ?? !!roomIdFromToken(token),
        isAuthenticated: true,
        isIdle: false,
        isLoading: false,
        isError: false,
        guestExpired: false,
        guestName: res.data.name,
        bootError: null,
      })
    );
    return 'ok';
  } catch (err) {
    if (isAuthError(err)) {
      LoggerService.info('Guest session rejected, offering recovery');
      await markExpired(profile?.name || null);
      return 'expired';
    }
    LoggerService.warn('Guest session check failed (network)');
    dispatch(
      updateUserState({
        isIdle: false,
        isLoading: false,
        bootError: 'network',
        guestName: profile?.name || null,
      })
    );
    return 'network';
  }
};

/**
 * Restore a guest on app start. Resolves to false when there is no guest
 * profile, so the caller falls back to the existing Firebase/account flow.
 */
export const bootstrapGuestSession = async (): Promise<boolean> => {
  const profile = await loadGuestProfile();
  if (!profile) return false;

  const token = await readItem(TOKEN_KEY);
  if (!token) {
    await markExpired(profile.name);
    return true;
  }
  await refreshGuestUser(token, profile);
  return true;
};

export const startGuestSession = async (rawName: string) => {
  const name = normalizeGuestName(rawName);
  const current = getStoreState().userReducer;
  const existingToken =
    current.userType === 'guest' &&
    current.token &&
    !isTokenExpired(current.token)
      ? current.token
      : null;
  const res = await api.post<{ token: string; user: GuestUser }>(
    '/guest-session',
    { name },
    {
      skipAuthRedirect: true,
      ...(existingToken ? { headers: authHeader(existingToken) } : {}),
    }
  );
  const token = extractToken(res.data);
  if (!token) throw new Error('Guest session response had no token');
  const user = res.data.user || ({ id: '', name } as GuestUser);

  await BhabiStorage.setToken(token);
  await saveGuestProfile({
    kind: 'guest',
    name: user.name || name,
    userId: user.id,
    createdAt: Date.now(),
  });
  dispatch(
    updateUserState({
      ...user,
      userType: user.userType || 'guest',
      token,
      isInRoom: !!roomIdFromToken(token),
      isAuthenticated: true,
      isIdle: false,
      isLoading: false,
      isError: false,
      guestExpired: false,
      guestName: user.name || name,
      bootError: null,
    })
  );
  return { token, user };
};

/** Store a (room-scoped or lobby) token as the active credential. */
export const applyToken = async (token: string) => {
  await BhabiStorage.setToken(token);
  dispatch(
    updateUserState({
      token,
      isInRoom: !!roomIdFromToken(token),
    })
  );
};

// ── room operations (shared contract, see handoff) ───────────────────────────

export const createTable = async (token: string, numberOfPlayers: number) => {
  const res = await api.post(
    '/create-room',
    { numberOfPlayers },
    { headers: authHeader(token) }
  );
  const roomToken = extractToken(res.data);
  if (!roomToken) throw new Error('Create table response had no token');
  return roomToken;
};

export const joinTable = async (token: string, roomID: string) => {
  const res = await api.post(
    '/re-join-room',
    { roomID },
    { headers: authHeader(token) }
  );
  const roomToken = extractToken(res.data);
  if (!roomToken) throw new Error('Join table response had no token');
  return roomToken;
};

export const leaveTable = async (token: string) => {
  const res = await api.get('/leave-room', { headers: authHeader(token) });
  const lobbyToken = extractToken(res.data);
  if (!lobbyToken) throw new Error('Leave table response had no token');
  dispatch(setInPlayState(emptyInPlay));
  return lobbyToken;
};

export const inviteBots = (token: string, numberOfBots: number) =>
  api.post(
    '/bot/invite-bots',
    { numberOfBots },
    { headers: authHeader(token) }
  );

export const rematchTable = (token: string) =>
  api.get('/re-match', { headers: authHeader(token) });

/** Public lookup. Resolves null for unknown tables (404). */
export const fetchInvitation = async (
  roomID: string
): Promise<Invitation | null> => {
  try {
    const res = await api.get<Invitation>(
      `/invitations/${encodeURIComponent(roomID)}`,
      { skipAuthRedirect: true }
    );
    return res.data;
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    if (status === 404 || status === 400) return null;
    throw err;
  }
};

// ── selectors ────────────────────────────────────────────────────────────────

export const useSessionInfo = () => {
  const user = useSelector((state: Store) => state.userReducer);
  const isGuest = user.userType === 'guest';
  const isAccount = !!user.isAuthenticated && !isGuest;
  return {
    user,
    isGuest: isGuest && !!user.isAuthenticated,
    isAccount,
    hasSession: !!user.isAuthenticated,
    token: user.token,
    name: user.name || user.guestName || '',
    roomID: roomIdFromToken(user.token),
    isInRoom: !!user.isInRoom || !!roomIdFromToken(user.token),
    guestExpired: !!user.guestExpired,
    bootError: user.bootError,
  };
};
